// Bankabgleich (docs/bank-datev/SPEZIFIKATION.md §8): Bankkonten, Kontoauszüge im Format camt.053 einlesen, Umsätze mit
// Zuordnungsvorschlag, bestätigen (einzeln oder gesammelt), auf ein Sachkonto buchen, Zuordnung stornieren.
// Gebucht wird NUR über die vorhandenen Zahlungsaktionen (finance.payment.record, finance.payable.pay,
// finance.payment.reverse) – der Abgleich hält fest, welcher Umsatz zu welcher Zahlung gehört. Beträge in Cent;
// Umsätze mit Vorzeichen (Gutschrift +, Belastung −). Ordaneo ist nicht mit der Bank verbunden: Die Datei lädt man im
// Online-Banking herunter.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zAmountCents, zAmountOrZeroCents, zDay, zPartnerRef, zReason } from './common'

const READ = 'finance.bank.read'
const IMPORT = 'finance.bank.import'
const MATCH = 'finance.bank.match'
const BOOK = 'finance.bank.book'
const ACCOUNTS = 'finance.bank.accounts'

export const MATCH_LEVELS = ['high', 'medium', 'low'] as const
export const BANK_STATES = ['open', 'partial', 'matched'] as const
export const BANK_STATE_LABELS: Record<(typeof BANK_STATES)[number], string> = { open: 'offen', partial: 'teilweise zugeordnet', matched: 'zugeordnet' }

/** Hinweis an Bank-Seite und Vorschau: keine Verbindung zur Bank, keine Zusage über das Format hinaus */
export const BANK_NOTICE = 'Ordaneo ist nicht mit Ihrer Bank verbunden: Laden Sie den Kontoauszug im Format camt.053 im Online-Banking herunter und lesen Sie ihn hier ein.'

const zIban = z
  .string({ error: 'Bitte die IBAN eingeben.' })
  .transform((value) => value.replace(/\s+/g, '').toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/, { error: 'Die IBAN hat nicht das richtige Format.' }))
const zBic = z
  .string()
  .transform((value) => value.replace(/\s+/g, '').toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, { error: 'Der BIC hat 8 oder 11 Zeichen (z. B. BYLADEM1FSI).' }))
const zLedgerAccount = z.string({ error: 'Bitte ein Sachkonto eingeben.' }).trim().regex(/^[0-9]{3,9}$/, { error: 'Das Sachkonto besteht aus 3 bis 9 Ziffern.' })
const zTaxKey = z.string().trim().regex(/^[0-9]{1,4}$/, { error: 'Der Steuerschlüssel besteht aus 1 bis 4 Ziffern.' })

// ---- Bankkonten -----------------------------------------------------------------------------------------------------

export const zBankAccount = z.object({
  id: zId,
  code: z.string(),
  name: z.string(),
  iban: z.string(),
  bic: z.string().nullable(),
  ledgerAccount: z.string(),
  active: z.boolean(),
  version: z.int(),
  /** Schlusssaldo des letzten eingelesenen Auszugs */
  balanceCents: z.int().nullable(),
  balanceDate: z.string().nullable(),
  lastStatementAt: z.date().nullable(),
  openCount: z.int(),
  openCents: z.int(),
})

export const bankAccountList = defineContract({
  name: 'finance.bank.accountList',
  title: 'Bankkonten anzeigen',
  description: 'Die Bankkonten des Betriebs mit Sachkonto, letztem Saldo und Zahl der offenen Umsätze.',
  llm: {
    description: 'Listet die Bankkonten des Betriebs: Kürzel, Bezeichnung, IBAN, BIC, Sachkonto im Export, Schlusssaldo des letzten eingelesenen Kontoauszugs, Zahl und Summe der noch nicht zugeordneten Umsätze. Verändert nichts.',
    keywords: ['Bankkonten', 'Kontostand', 'IBAN', 'offene Umsätze'],
  },
  input: z.strictObject({ includeInactive: z.boolean().default(false) }),
  output: z.object({ rows: z.array(zBankAccount), notice: z.string() }),
  permission: READ,
  risk: 'read',
})

export const bankAccountSave = defineContract({
  name: 'finance.bank.accountSave',
  title: 'Bankkonto speichern',
  description: 'Legt ein Bankkonto an oder ändert es: Kürzel, Bezeichnung, IBAN, BIC, Sachkonto.',
  llm: {
    description: 'Legt ein Bankkonto des Betriebs an (ohne `id`) oder ändert es (mit `id` und `expectedVersion`). `ledgerAccount` ist das Sachkonto der Bank im Kontenrahmen (z. B. 1200 im SKR 03, 1800 im SKR 04) – mit der Steuerkanzlei abstimmen. `code` ist ein Kürzel aus bis zu 6 Großbuchstaben oder Ziffern für die Belegnummer der Buchungen auf Sachkonten. Die IBAN eines Kontos mit eingelesenen Auszügen lässt sich nicht mehr ändern.',
    keywords: ['Bankkonto anlegen', 'IBAN hinterlegen', 'Sachkonto Bank'],
  },
  input: z.strictObject({
    id: zId.optional(),
    code: z.string({ error: 'Bitte ein Kürzel eingeben.' }).trim().toUpperCase().regex(/^[A-Z0-9]{1,6}$/, { error: 'Das Kürzel besteht aus 1 bis 6 Großbuchstaben oder Ziffern.' }),
    name: z.string({ error: 'Bitte eine Bezeichnung eingeben.' }).trim().min(2, { error: 'Bitte eine Bezeichnung eingeben.' }).max(80),
    iban: zIban,
    bic: zBic.nullish(),
    ledgerAccount: zLedgerAccount,
    active: z.boolean().default(true),
    expectedVersion: z.int().optional(),
  }),
  output: z.object({ id: zId, message: z.string() }),
  permission: ACCOUNTS,
  risk: 'write',
})

// ---- Kontoauszüge ------------------------------------------------------------------------------------------------

export const zBankStatement = z.object({
  id: zId,
  bankAccount: z.object({ id: zId, code: z.string(), name: z.string() }),
  reference: z.string(),
  electronicSeq: z.int().nullable(),
  format: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  openingCents: z.int().nullable(),
  closingCents: z.int().nullable(),
  creditCents: z.int(),
  debitCents: z.int(),
  entryCount: z.int(),
  importedCount: z.int(),
  duplicateCount: z.int(),
  pendingCount: z.int(),
  /** davon zugeordnet (Stand jetzt) */
  matchedCount: z.int(),
  importedAt: z.date(),
  importedByName: z.string().nullable(),
  fileId: zId,
  fileUrl: z.string(),
  /** Anfangssaldo ≠ Schlusssaldo des vorigen Auszugs */
  gapCents: z.int().nullable(),
})

export const bankStatementImport = defineContract({
  name: 'finance.bank.statementImport',
  title: 'Kontoauszug einlesen',
  description: 'Liest eine hochgeladene Datei im Format camt.053 ein: jeden gebuchten Umsatz genau einmal, mit Saldo-Probe.',
  llm: {
    description:
      'Liest einen Kontoauszug im Format camt.053 (Datei vorher über POST /api/files?kind=camt hochladen, dann `fileId`) ein. ' +
      'Das Konto wird an der IBAN erkannt und muss als Bankkonto angelegt sein. Schon eingelesene Auszüge (gleiche Kennung) ' +
      'und schon vorhandene Umsätze (gleiche Referenz der Bank bzw. gleicher Fingerabdruck) werden übersprungen und gezählt. ' +
      'Vorgemerkte Umsätze werden nicht übernommen. Danach stehen die Umsätze mit Vorschlag im Abgleich (finance.bank.transactionList).',
    keywords: ['Kontoauszug einlesen', 'camt.053', 'Bankdatei', 'Umsätze importieren'],
  },
  input: z.strictObject({ fileId: zId }),
  output: z.object({
    statements: z.array(z.object({ id: zId.nullable(), bankAccountName: z.string(), reference: z.string(), from: z.string().nullable(), to: z.string().nullable(), importedCount: z.int(), duplicateCount: z.int(), pendingCount: z.int(), duplicateStatement: z.boolean() })),
    importedCount: z.int(),
    duplicateCount: z.int(),
    warnings: z.array(z.string()),
    message: z.string(),
  }),
  permission: IMPORT,
  risk: 'write',
})

export const bankStatementList = defineContract({
  name: 'finance.bank.statementList',
  title: 'Kontoauszüge anzeigen',
  description: 'Eingelesene Kontoauszüge mit Zeitraum, Salden, Zahl der Umsätze und Stand des Abgleichs.',
  llm: {
    description: 'Listet eingelesene Kontoauszüge, neueste zuerst: Konto, Kennung und Folgenummer, Zeitraum, Anfangs- und Schlusssaldo, Gutschriften und Belastungen, übernommene, doppelte und vorgemerkte Umsätze, wie viele schon zugeordnet sind, und eine Lücke zum vorigen Auszug. Verändert nichts.',
    keywords: ['Kontoauszüge', 'eingelesen', 'Salden', 'Lücke'],
  },
  input: z.strictObject({ bankAccountId: zId.optional(), limit: z.int().min(1).max(200).default(50) }),
  output: z.object({ rows: z.array(zBankStatement) }),
  permission: READ,
  risk: 'read',
})

// ---- Umsätze und Vorschläge ---------------------------------------------------------------------------------------

/** Eine Zeile einer Zuordnung: Was beim Bestätigen gebucht bzw. verknüpft wird. */
export const zBankLine = z.discriminatedUnion('kind', [
  z.strictObject({
    /** Zahlung buchen über finance.payment.record (Richtung aus dem Umsatz) */
    kind: z.literal('payment'),
    partnerId: zId,
    amountCents: zAmountCents,
    allocations: z
      .array(
        z.strictObject({
          openItemId: zId,
          amountCents: zAmountOrZeroCents,
          discountCents: zAmountOrZeroCents.default(0),
          discountReason: z.string().trim().max(300).optional(),
        }),
      )
      .max(200)
      .default([]),
  }),
  /** Eingangsrechnungen über finance.payable.pay (wie im Zahlungsvorschlag, Gutschriften verrechnet) */
  z.strictObject({ kind: z.literal('payable'), amountCents: zAmountCents, openItemIds: z.array(zId).min(1).max(200) }),
  /** schon gebuchte Zahlung nur verknüpfen – es wird nichts gebucht */
  z.strictObject({ kind: z.literal('link'), paymentId: zId }),
  /** Rücklastschrift: die ursprüngliche Zahlung stornieren (finance.payment.reverse) und den Umsatz damit verknüpfen */
  z.strictObject({ kind: z.literal('reverse'), paymentId: zId }),
])

export const zBankSuggestion = z.object({
  key: z.string(),
  level: z.enum(MATCH_LEVELS),
  levelLabel: z.string(),
  partner: zPartnerRef.nullable(),
  title: z.string(),
  reasons: z.array(z.string()),
  hints: z.array(z.string()),
  lines: z.array(zBankLine),
  items: z.array(z.object({ openItemId: zId, number: z.string().nullable(), openCents: z.int(), amountCents: z.int(), discountCents: z.int(), restCents: z.int() })),
  unallocatedCents: z.int(),
})

export const zBankMatch = z.object({
  id: zId,
  kind: z.enum(['payment', 'ledger']),
  origin: z.enum(['booked', 'linked']),
  amountCents: z.int(),
  partner: zPartnerRef.nullable(),
  paymentId: zId.nullable(),
  /** Belegnummern der ausgeglichenen Posten */
  numbers: z.array(z.string()),
  account: z.string().nullable(),
  taxKey: z.string().nullable(),
  text: z.string().nullable(),
  createdAt: z.date(),
  createdByName: z.string().nullable(),
  approvedByName: z.string().nullable(),
  reversed: z.boolean(),
  reversedAt: z.date().nullable(),
  reverseReason: z.string().nullable(),
})

export const zBankTransactionRow = z.object({
  id: zId,
  bankAccount: z.object({ id: zId, code: z.string(), name: z.string() }),
  statementId: zId,
  bookingDate: z.string(),
  valueDate: z.string().nullable(),
  /** mit Vorzeichen: Gutschrift +, Belastung − */
  amountCents: z.int(),
  counterpartyName: z.string().nullable(),
  counterpartyIban: z.string().nullable(),
  remittance: z.string(),
  endToEndId: z.string().nullable(),
  bankText: z.string().nullable(),
  returned: z.boolean(),
  state: z.enum(BANK_STATES),
  stateLabel: z.string(),
  /** Summe der geltenden Zuordnungen (mit Vorzeichen) */
  matchedCents: z.int(),
  /** bester Vorschlag (nur bei offenen Umsätzen) */
  suggestion: zBankSuggestion.nullable(),
  /** Kurzform der Zuordnung, z. B. „Bella Isar · RE-2026-00012“ */
  matchSummary: z.string().nullable(),
})

export const BANK_SORT_KEYS = ['bookingDate', 'amountCents'] as const

export const bankTransactionList = defineContract({
  name: 'finance.bank.transactionList',
  title: 'Bankumsätze im Abgleich anzeigen',
  description: 'Umsätze der eingelesenen Kontoauszüge mit Stand und bestem Zuordnungsvorschlag.',
  llm: {
    description:
      'Listet Bankumsätze: Buchungstag, Betrag in Cent (Gutschrift +, Belastung −), Gegenseite, Verwendungszweck, Stand ' +
      '(open, partial, matched) und – bei offenen – den besten Vorschlag mit Trefferstufe (high = sicher, medium = ' +
      'wahrscheinlich, low = möglich), Begründung und den Zeilen, die finance.bank.confirm bucht. Filter: Konto, Stand, ' +
      'Richtung, Zeitraum, Auszug. `q` sucht in Name, IBAN und Verwendungszweck. Verändert nichts.',
    keywords: ['Bankabgleich', 'Bankumsätze', 'offene Umsätze', 'Zuordnungsvorschlag', 'Kontoauszug zuordnen'],
  },
  input: listInput({
    sortBy: BANK_SORT_KEYS,
    defaultSort: { by: 'bookingDate', dir: 'desc' },
    filters: {
      bankAccountId: zId.optional(),
      state: z.enum(['open', 'matched', 'all']).optional(),
      direction: z.enum(['credit', 'debit']).optional(),
      from: zDay.optional(),
      to: zDay.optional(),
      statementId: zId.optional(),
    },
  }),
  output: listOutput(zBankTransactionRow),
  permission: READ,
  risk: 'read',
})

export const bankTransactionGet = defineContract({
  name: 'finance.bank.transactionGet',
  title: 'Bankumsatz anzeigen',
  description: 'Ein Umsatz mit allen Angaben der Bank, bis zu drei Vorschlägen und den Zuordnungen samt Storno.',
  llm: {
    description: 'Liefert einen Bankumsatz vollständig: Angaben der Bank (Gegenseite, IBAN, BIC, Verwendungszweck, EndToEndId, Mandat, Geschäftsvorfall, Einzelumsätze eines Sammlers), bis zu drei Vorschläge mit Trefferstufe und Begründung, die Zuordnungen (auch stornierte) und was noch offen ist. Verändert nichts.',
    keywords: ['Bankumsatz', 'Vorschläge', 'Zuordnung ansehen'],
  },
  input: z.strictObject({ id: zId }),
  output: zBankTransactionRow.extend({
    counterpartyBic: z.string().nullable(),
    mandateId: z.string().nullable(),
    structuredRef: z.string().nullable(),
    bankRef: z.string().nullable(),
    gvc: z.string().nullable(),
    returnReason: z.string().nullable(),
    batchCount: z.int().nullable(),
    details: z.array(z.object({ name: z.string().nullable(), iban: z.string().nullable(), amountCents: z.int().nullable(), remittance: z.string(), endToEndId: z.string().nullable() })),
    statement: z.object({ id: zId, reference: z.string(), electronicSeq: z.int().nullable() }),
    suggestions: z.array(zBankSuggestion),
    matches: z.array(zBankMatch),
    /** noch nicht zugeordnet (positiv) */
    restCents: z.int(),
    /** Vorbelegung für „Auf Sachkonto buchen“ (Gebühren der Bank: Nebenkosten des Geldverkehrs im gewählten Kontenrahmen) */
    ledgerSuggestion: z.object({ account: z.string(), text: z.string() }).nullable(),
    canConfirm: z.boolean(),
    canBook: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
})

// ---- Bestätigen, buchen, stornieren -----------------------------------------------------------------------------

const zConfirmed = z.object({
  transactionId: zId,
  matchIds: z.array(zId),
  paymentIds: z.array(zId),
  state: z.enum(BANK_STATES),
  message: z.string(),
})

export const bankConfirm = defineContract({
  name: 'finance.bank.confirm',
  title: 'Bankumsatz zuordnen',
  description: 'Bestätigt eine Zuordnung: bucht die Zahlung über die vorhandenen Zahlungsaktionen bzw. verknüpft eine schon gebuchte.',
  llm: {
    description:
      'Ordnet einen Bankumsatz zu. `lines` kommen in der Regel aus dem Vorschlag (finance.bank.transactionList / ' +
      'transactionGet): `payment` bucht über finance.payment.record (Richtung aus dem Umsatz, Wertstellung = Buchungstag, ' +
      'Zahlart Überweisung bzw. Lastschrift) mit den genannten Posten und Skonto; `payable` bucht Eingangsrechnungen über ' +
      'finance.payable.pay; `link` verknüpft eine schon gebuchte Zahlung (bucht nichts); `reverse` storniert bei einer ' +
      'Rücklastschrift die ursprüngliche Zahlung. Die Summe darf den Betrag des Umsatzes nicht übersteigen; was übrig ' +
      'bleibt, lässt sich auf ein Sachkonto buchen (finance.bank.book). Braucht zusätzlich die Rechte der Zahlungsaktionen.',
    keywords: ['Bankumsatz zuordnen', 'Vorschlag bestätigen', 'Zahlungseingang aus Kontoauszug'],
  },
  input: z.strictObject({ transactionId: zId, lines: z.array(zBankLine).min(1, { error: 'Bitte mindestens eine Zuordnung wählen.' }).max(20) }),
  output: zConfirmed,
  permission: MATCH,
  risk: 'financial',
  effects: { reversible: true },
})

export const bankConfirmMany = defineContract({
  name: 'finance.bank.confirmMany',
  title: 'Bankumsätze gesammelt zuordnen',
  description: 'Bestätigt mehrere Zuordnungen auf einmal – jede für sich: Eine, die nicht mehr passt, hält die übrigen nicht auf.',
  llm: {
    description: 'Bestätigt die Vorschläge mehrerer Bankumsätze in einem Schritt (je Umsatz die `lines` wie bei finance.bank.confirm). Jede Zuordnung läuft für sich; scheitert eine (Posten inzwischen bezahlt, Periode gesperrt), stehen der Umsatz und der Satz unter `failed`, die übrigen sind gebucht. Gedacht für Vorschläge mit Trefferstufe „sicher“.',
    keywords: ['alle sicheren bestätigen', 'gesammelt zuordnen', 'Sammelbestätigung'],
  },
  input: z.strictObject({ items: z.array(z.strictObject({ transactionId: zId, lines: z.array(zBankLine).min(1).max(20) })).min(1, { error: 'Bitte mindestens einen Umsatz wählen.' }).max(100) }),
  output: z.object({ confirmed: z.array(zConfirmed), failed: z.array(z.object({ transactionId: zId, message: z.string() })), message: z.string() }),
  permission: MATCH,
  risk: 'financial',
  limits: { maxItems: 100, itemCount: (input) => input.items.length },
})

export const bankBook = defineContract({
  name: 'finance.bank.book',
  title: 'Bankumsatz auf ein Sachkonto buchen',
  description: 'Bucht einen Umsatz ohne offenen Posten (Gebühr, Miete, Geldtransit) – ganz oder den Rest – auf ein Sachkonto.',
  llm: {
    description:
      'Bucht einen Bankumsatz (oder seinen noch offenen Rest) auf ein Sachkonto, z. B. Kontoführungsgebühren (SKR 03: ' +
      '4970), Geldtransit (1360) oder Privatentnahme. `account` 3–9 Ziffern, `taxKey` optional (BU-Schlüssel, nur wenn ' +
      'das Konto kein Automatikkonto ist), `text` erscheint als Buchungstext. Im Buchungsjournal und im DATEV-Export steht ' +
      'die Buchung Bank an Sachkonto bzw. umgekehrt. Über der Grenze der Rolle bestätigt eine zweite Person mit PIN. ' +
      'Konten mit der Steuerkanzlei abstimmen.',
    keywords: ['auf Sachkonto buchen', 'Bankgebühren', 'Kontoführung', 'Geldtransit', 'nicht zuordenbar'],
  },
  input: z.strictObject({
    transactionId: zId,
    account: zLedgerAccount,
    taxKey: zTaxKey.nullish(),
    text: z.string().trim().max(60, { error: 'Der Buchungstext hat höchstens 60 Zeichen.' }).nullish(),
    /** Teilbetrag (positiv); ohne Angabe der ganze offene Rest */
    amountCents: zAmountCents.optional(),
  }),
  output: zConfirmed,
  permission: BOOK,
  risk: 'financial',
  secondPerson: true,
  effects: { reversible: true },
})

export const bankUnmatch = defineContract({
  name: 'finance.bank.unmatch',
  title: 'Zuordnung eines Bankumsatzes stornieren',
  description: 'Storniert alle geltenden Zuordnungen eines Umsatzes: Gebuchte Zahlungen werden storniert, Buchungen auf Sachkonten gegengebucht, Verknüpfungen gelöst.',
  llm: {
    description:
      'Nimmt die Zuordnung eines Bankumsatzes zurück – nur per Storno, nichts wird gelöscht: Zahlungen, die beim ' +
      'Bestätigen gebucht wurden, storniert finance.payment.reverse (Posten wieder offen); Buchungen auf Sachkonten ' +
      'erscheinen mit Gegenbuchung am heutigen Tag im Journal; nur verknüpfte Zahlungen bleiben gebucht und sind wieder ' +
      'frei. Danach ist der Umsatz wieder offen. Braucht einen Grund.',
    keywords: ['Zuordnung stornieren', 'Bankabgleich rückgängig', 'falsch zugeordnet'],
  },
  input: z.strictObject({ transactionId: zId, reason: zReason }),
  output: z.object({ transactionId: zId, reversedCount: z.int(), reversedPaymentIds: z.array(zId), message: z.string() }),
  permission: MATCH,
  risk: 'destructive',
})
